import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { create, getById, update } from "../../../services/customers.service";
import { AlertService } from "../../../services/alert.service";
import { useMutation, useQuery } from "react-query";
import ConfirmModal from "../../../components/Commons/Modal/confirm-modal.component";
import InputMask from "react-input-mask";
import { findAddressByCep } from "../../../services/viacep.service";

const { TextArea } = Input;

export default function FormCustomer(props: any) {
  const [active, setActive] = useState<boolean>(true);
  const [leaveThePage, setLeaveThePage] = useState<boolean>(false);
  const [valuesChanged, setValuesChanged] = useState<boolean>(false);
  const [documentType, setDocumentType] = useState<string>("CPF");
  const [enablePassword, setEnablePassword] = useState<boolean>(true);

  const { id } = useParams();

  const [formCustomer] = Form.useForm();
  const navigate = useNavigate();

  const settings = {
    create: { title: `Criar novo cliente` },
    edit: { title: `Editar cliente` },
  };

  const goCustomersList = () => {
    navigate(`/management/customers`);
  };

  useQuery(["loadCustomer", id], () => (id ? getById(id) : null), {
    onSuccess: (response) => {
      if (response) {
        formCustomer.setFieldsValue(response.data);
        setActive(response.data.active);
        setEnablePassword(false);
      }
    },
    refetchOnWindowFocus: false,
  });

  const { refetch: onFindAddressByCep } = useQuery(
    ["loadCep"],
    () => {
      let postalCode = formCustomer.getFieldValue("postal_code");
      return findAddressByCep(postalCode.replaceAll("_", ""));
    },
    {
      onSuccess: (response) => {
        if (response) {
          formCustomer.setFieldsValue({
            address: response.data.logradouro,
            neighborhood: response.data.bairro,
            city: response.data.localidade,
            state: response.data.uf,
          });
        }
      },
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

  const { mutate: createCustomer, isLoading: createCustomerLoading } =
    useMutation(create, {
      onSuccess: () => {
        AlertService.sendAlert([
          {
            text: `Novo cliente gravado com sucesso.`,
            nextPage: true,
          },
        ]);

        goCustomersList();
      },
    });

  const { mutate: updateCustomer, isLoading: updateCustomerLoading } =
    useMutation(update, {
      onSuccess: () => {
        AlertService.sendAlert([
          {
            text: `Cliente editado com sucesso.`,
            nextPage: true,
          },
        ]);

        goCustomersList();
      },
    });

  const onSubmit = () => {
    formCustomer
      .validateFields()
      .then((values) => {
        if (values.vehicle_id === undefined) {
          values.vehicle_id = null;
        }

        if (!id) {
          createCustomer({ ...values, active });
        } else {
          updateCustomer({ values: { ...values, active }, id });
        }
      })
      .catch(() =>
        AlertService.sendAlert([
          { text: "Campo(s) com preenchimento inválido", type: "error" },
        ])
      );
  };

  const onChangeActive = (value: boolean) => {
    setActive(value);
  };

  const onChangeName = (value: string) => {
    if (formCustomer.getFieldValue("document_type") === "CPF") {
      formCustomer.setFieldValue("responsible_name", value);
    }
  };

  const onChangePhone = (value: string) => {
    if (formCustomer.getFieldValue("document_type") === "CPF") {
      formCustomer.setFieldValue("responsible_phone", value);
    }
  };

  const onChangeCep = (value: string) => {
    if (value.length === 9) {
      onFindAddressByCep();
    }
  };

  const fields = {
    id: (
      <Form.Item label="ID" name="id">
        <Input size="large" disabled={true} placeholder="000" />
      </Form.Item>
    ),
    name: (
      <Form.Item
        label="Cliente"
        name="name"
        rules={[{ required: true, message: "Campo obrigatório" }]}
      >
        <Input
          size="large"
          maxLength={100}
          onBlur={(event) => onChangeName(event.target.value)}
        />
      </Form.Item>
    ),
    phone: (
      <Form.Item
        label="Telefone"
        name="phone"
        rules={[{ required: true, message: "Campo obrigatório" }]}
      >
        <InputMask
          mask={"(99) 99999-9999"}
          autoComplete="off"
          onBlur={(event) => onChangePhone(event.target.value)}
        >
          <Input size="large" style={{ width: "100%" }} />
        </InputMask>
      </Form.Item>
    ),
    document_type: (
      <Form.Item label="Documento" name="document_type">
        <Select
          placeholder="Selecione"
          size="large"
          options={[
            { value: "CPF", label: "CPF" },
            { value: "CNPJ", label: "CNPJ" },
          ]}
          onChange={($event) => setDocumentType($event)}
        />
      </Form.Item>
    ),
    document:
      documentType === "CPF" ? (
        <Form.Item
          label="Nº Documento"
          name="document"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <InputMask mask={"999.999.999-99"} autoComplete="off">
            <Input size="large" style={{ width: "100%" }} />
          </InputMask>
        </Form.Item>
      ) : (
        <Form.Item
          label="Nº Documento"
          name="document"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <InputMask mask={"99.999.999/9999-99"} autoComplete="off">
            <Input size="large" style={{ width: "100%" }} />
          </InputMask>
        </Form.Item>
      ),
    responsible_name: (
      <Form.Item label="Responsável" name="responsible_name">
        <Input size="large" maxLength={100} />
      </Form.Item>
    ),
    responsible_phone: (
      <Form.Item label="Telefone do responsável" name="responsible_phone">
        <InputMask mask={"(99) 99999-9999"} autoComplete="off">
          <Input size="large" style={{ width: "100%" }} />
        </InputMask>
      </Form.Item>
    ),
    address: (
      <Form.Item label="Endereço" name="address">
        <Input size="large" showCount maxLength={255} />
      </Form.Item>
    ),
    postal_code: (
      <Form.Item
        label="CEP"
        name="postal_code"
        rules={[
          {
            pattern: /^([\d]{5})-*([\d]{3})/,
            message: "Formato do CEP inválido",
          },
        ]}
      >
        <InputMask
          mask={"99999-999"}
          autoComplete="off"
          onBlur={(event) => onChangeCep(event.target.value)}
        >
          <Input size="large" style={{ width: "100%" }} />
        </InputMask>
      </Form.Item>
    ),
    neighborhood: (
      <Form.Item label="Bairro" name="neighborhood">
        <Input size="large" maxLength={50} />
      </Form.Item>
    ),
    city: (
      <Form.Item label="Cidade" name="city">
        <Input size="large" maxLength={50} />
      </Form.Item>
    ),
    state: (
      <Form.Item label="Estado" name="state">
        <Input size="large" maxLength={50} />
      </Form.Item>
    ),
    username: (
      <Form.Item label="Usuário" name="username">
        <Input size="large" maxLength={50} />
      </Form.Item>
    ),
    password: !enablePassword ? (
      <Button
        size="large"
        block
        style={{ marginTop: 30 }}
        onClick={() => setEnablePassword(!enablePassword)}
      >
        Alterar senha
      </Button>
    ) : (
      <Form.Item
        name="password"
        label="Senha"
        data-html="true"
        tooltip={
          <>
            A senha deve conter: <br />
            - No mínimo 8 caracteres <br />- Letras maiúsculas, minúsculas,
            números e caracteres especiais
          </>
        }
        rules={[
          { min: 8, message: "A senha deve ter no mínimo 8 caracteres" },
          {
            pattern: new RegExp(
              "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[-+_!@#$%^&*.,?]).+$"
            ),
            message:
              "A senha deve ter letras maiúsculas, minúsculas, números e caracteres especiais",
          },
        ]}
      >
        <Space.Compact block>
          <Input.Password
            type="password"
            placeholder="Senha"
            size="large"
            maxLength={100}
          />

          {props.action === "edit" && (
            <Button
              size="large"
              onClick={() => setEnablePassword(!enablePassword)}
            >
              Cancelar
            </Button>
          )}
        </Space.Compact>
      </Form.Item>
    ),
    email: (
      <Form.Item
        label="E-mail"
        name="email"
        rules={[
          { required: true, message: "Campo obrigatório" },
          { type: "email", message: "Formato de e-mail inválido" },
        ]}
      >
        <Input type="email" size="large" maxLength={50} />
      </Form.Item>
    ),
    notes: (
      <Form.Item label="Observações" name="notes">
        <TextArea size="large" showCount maxLength={255} />
      </Form.Item>
    ),
  };

  useEffect(() => {
    if (!id) {
      formCustomer.setFieldValue("document_type", documentType);
    }
  }, []);

  return (
    <>
      <Card
        styles={{
          header: {
            color: "#0899d1",
            fontWeight: 600,
          },
        }}
        title={settings[props.action as keyof typeof settings].title}
      >
        <Form
          form={formCustomer}
          name="form_customer"
          layout="vertical"
          className="form_customer"
          autoComplete="off"
          onValuesChange={() => setValuesChanged(true)}
        >
          {(props.formFields || []).map((row: any, indexRow: number) => {
            return (
              <Row key={indexRow} gutter={20}>
                {row.cols.map((col: any, indexCol: number) => {
                  return (
                    <Col
                      key={indexCol}
                      lg={{ span: col.lg }}
                      md={{ span: col.md }}
                      sm={{ span: col.sm }}
                      xs={{ span: col.xs }}
                    >
                      {fields[col.field as keyof typeof fields]}
                    </Col>
                  );
                })}
              </Row>
            );
          })}

          <Row
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Col>
              {id && (
                <Form.Item
                  label={`Cliente ${active ? "ativo" : "não ativo"}`}
                  name="active"
                >
                  <Switch checked={active} onChange={onChangeActive} />
                </Form.Item>
              )}
            </Col>
            <Col>
              <Space>
                <Button
                  loading={createCustomerLoading || updateCustomerLoading}
                  size="large"
                  onClick={() => {
                    if (!id || valuesChanged) {
                      setLeaveThePage(true);
                    } else {
                      goCustomersList();
                    }
                  }}
                >
                  Cancelar
                </Button>

                <Button
                  type="primary"
                  loading={createCustomerLoading || updateCustomerLoading}
                  size="large"
                  onClick={onSubmit}
                >
                  Gravar
                </Button>
              </Space>
            </Col>
          </Row>

          <ConfirmModal
            open={leaveThePage}
            title="Tem certeza de que deseja sair sem gravar as alterações?"
            content="Toda a informação não gravada será perdida."
            okText="Sair"
            cancelText="Voltar"
            onOk={() => {
              setLeaveThePage(false);
              goCustomersList();
            }}
            onCancel={() => setLeaveThePage(false)}
          ></ConfirmModal>
        </Form>
      </Card>
    </>
  );
}
